'use client';
import type { FC } from 'react';
import type { Swiper as SwiperCore } from 'swiper';
import type { SwiperProps } from 'swiper/swiper-react';
import type { ConvertedExhibitionProductWidgetData } from '@weberus/shared-types/src';
import type { HomeLogPageNameValue } from '@/app/(pages)/sub-home/[category]/constants';
import { useRef } from 'react';
import { IcArrowforwardFill } from '@yanolja-rnd/nol-icons';

import { RectangleButton, Thumbnail } from '@yanolja-rnd/nol-design-system';
import { Swiper, SwiperSlide } from 'swiper/react';
import { clientLogStandardized, YaTrackerImpression } from '@weberus/client-tracker';
import { VerticalProductItem } from '@weberus/ui/components';
import { SwiperNavButton } from '@/app/_components/common';

export interface ExhibitionProductProps {
  data: ConvertedExhibitionProductWidgetData;
  pageNameLog: HomeLogPageNameValue;
}

const swiperConfig: SwiperProps = {
  spaceBetween: 0,
  slidesOffsetBefore: 0,
  slidesOffsetAfter: 0,
  breakpoints: {
    500: {
      loop: true,
    },
  },
};
export const ExhibitionProductPC: FC<ExhibitionProductProps> = ({ data, pageNameLog }) => {
  const { items, header } = data;
  const swiperRef = useRef<SwiperCore | null>(null);
  const widgetPosition = data.logMeta.widgetPosition;

  const showHeader = !!header?.title || !!header?.subTitle;
  const showNavButton = items.length >= 2;

  return (
    <section className="my-[4rem] flex w-full justify-center">
      <div className="pc:w-pc-size">
        {!!showHeader && (
          <div className="flex min-h-[8rem] items-center gap-[2rem] py-[0.4rem]">
            <h2 className="typography-headline-24-bold">{header?.title}</h2>
            <strong className="typography-subtitle-20-regular">{header?.subTitle}</strong>
          </div>
        )}
        <div className="relative">
          <Swiper
            className="overflow-hidden rounded-16 border-[0.1rem] border-opacity-black-16"
            {...swiperConfig}
            onSwiper={(swiper: SwiperCore) => {
              swiperRef.current = swiper;
            }}>
            {items.map((item) => (
              <SwiperSlide key={item.header?.title}>
                <SwiperItem
                  item={item}
                  widgetPosition={widgetPosition}
                  headerTitle={item.header?.title}
                  pageNameLog={pageNameLog}
                />
              </SwiperSlide>
            ))}
          </Swiper>
          {!!showNavButton && <SwiperNavButton swiperRef={swiperRef} />}
        </div>
      </div>
    </section>
  );
};

interface ItemProps {
  item: ConvertedExhibitionProductWidgetData['items'][0];
  widgetPosition?: number;
  headerTitle?: string;
  pageNameLog: HomeLogPageNameValue;
}

const SwiperItem: FC<ItemProps> = ({ item, widgetPosition, headerTitle, pageNameLog }) => {
  const widgetPagePosition = item.logMeta.position;

  return (
    <div className="flex h-[44.8rem] flex-row">
      <YaTrackerImpression
        onImpressed={() =>
          clientLogStandardized('impression', pageNameLog, {
            widgetPosition,
            widgetPagePosition,
            objectContainer: 'exhibitionProduct',
            ...item.logMeta,
          })
        }>
        <LeftContent item={item} widgetPosition={widgetPosition} pageNameLog={pageNameLog} />
      </YaTrackerImpression>
      {/* 아이템 그리기*/}
      <div className="relative m-36 mb-24 flex w-full justify-between gap-20">
        {item.items.map(({ productTapProps, logMeta }) => (
          <div
            key={productTapProps.id}
            className="w-[240px]"
            onClick={() =>
              clientLogStandardized('click', pageNameLog, {
                widgetPosition,
                widgetPagePosition,
                exhibitionTitle: headerTitle,
                objectContainer: 'exhibitionProduct',
                ...logMeta,
              })
            }>
            <div aria-hidden>
              <YaTrackerImpression
                onImpressed={() =>
                  clientLogStandardized('impression', pageNameLog, {
                    widgetPosition,
                    widgetPagePosition,
                    exhibitionTitle: headerTitle,
                    objectContainer: 'exhibitionProduct',
                    ...logMeta,
                  })
                }>
                <div />
              </YaTrackerImpression>
            </div>
            <VerticalProductItem {...productTapProps} />
          </div>
        ))}
      </div>
    </div>
  );
};

const LeftContent: FC<ItemProps> = ({ item, widgetPosition, pageNameLog }) => {
  const widgetPagePosition = item.logMeta.position;
  const onClickDetail = () => {
    clientLogStandardized('click', pageNameLog, {
      widgetPosition,
      widgetPagePosition,
      objectContainer: 'exhibitionProduct',
      ...item.logMeta,
    });
    if (item.header?.more.action.web) {
      location.href = item.header.more.action.web;
    }
  };
  return (
    <div className="relative h-[44.8rem] w-[44.8rem]">
      {/* Thumbnail에 사각형 rounded 설정이 없어서 구현함. 썸네일은 상품형 사진에 국한되서 사용하도록 설계되어있으므로 확장하여 구현한다. */}
      <Thumbnail
        alt=""
        image={item.backgroundImageUrl}
        className="h-[44.8rem] w-[44.8rem] rounded-none pc:rounded-none"
        imgClassName="object-top"
        ratio="1_1"
        shape="roundedRectangle_16"
      />

      <div className="absolute left-0 top-0 flex h-[44.8rem] w-[44.8rem] items-center justify-center bg-opacity-black-30">
        <div className="flex h-[35.2rem] w-[35.2rem] flex-col justify-between p-8">
          <div>
            {!!item.header?.title && (
              <h2 className="text-colors-white typography-headline-32-bold-line-clamp-2">{item.header.title}</h2>
            )}
          </div>
          {!!item.header?.more && (
            <RectangleButton
              theme="filled_basic"
              weight="bold"
              icon={<IcArrowforwardFill />}
              direction="right"
              isCapsule
              onClick={onClickDetail}>
              <span className="pl-6">{item.header.more.title}</span>
            </RectangleButton>
          )}
        </div>
      </div>
    </div>
  );
};
